// The client's view of the current room, assembled from bus events: the latest RoomStateDTO, the
// local seat, the last round result and the match result. Read by screens, HUD and debug hooks.
import { TICK_HZ } from '@shared/constants';
import { RoomPhase } from '@shared/ids';
import type { MatchEndDTO, PlayerDTO, RoomStateDTO, RoundEndDTO } from '@shared/protocol/messages';
import type { Bus } from '../core/bus';

const TICK_MS = 1000 / TICK_HZ;

export const ROOM_PHASE_NAMES: Readonly<Record<RoomPhase, string>> = {
  [RoomPhase.LOBBY]: 'LOBBY', [RoomPhase.INTRO]: 'INTRO', [RoomPhase.PLAY]: 'PLAY', [RoomPhase.ROUND_END]: 'ROUND_END',
  [RoomPhase.INTERMISSION]: 'INTERMISSION', [RoomPhase.AWARDS]: 'AWARDS', [RoomPhase.PODIUM]: 'PODIUM',
};

export class RoomState {
  dto: RoomStateDTO | null = null;
  code = '';
  slot = -1;
  spectator = false;
  isHost = false;
  roundEnd: RoundEndDTO | null = null;
  /** Every round result of the current match, in order (share text, Bean Card). */
  readonly roundEnds: RoundEndDTO[] = [];
  matchEnd: MatchEndDTO | null = null;
  /** My card vote this phase: my last press, replaced by the server's record (`voters`) once it has one. */
  myVote = -1;
  private receivedAt = 0;
  private readonly listeners: (() => void)[] = [];

  constructor(bus: Bus, private readonly clock: () => number) {
    bus.on('joined', (j) => {
      this.code = j.code;
      this.slot = j.slot;
      this.spectator = j.spectator;
      this.isHost = j.isHost;
      this.changed();
    });
    bus.on('room', (r) => {
      if (this.dto && this.dto.phase !== r.phase) this.myVote = -1;
      // Back in the lobby: the match is over and its results are forgotten.
      if (r.phase === RoomPhase.LOBBY && this.dto && this.dto.phase !== RoomPhase.LOBBY) {
        this.roundEnds.length = 0;
        this.matchEnd = null;
        this.roundEnd = null;
      }
      this.dto = r;
      // The server's record of my vote (WP3 `voters`: UI cards and the lobby's vote pads, newest
      // wins) replaces my last press once it has one.
      const voted = this.slot >= 0 ? r.voters?.[this.slot] : undefined;
      if (voted !== undefined && voted >= 0) this.myVote = voted;
      this.code = r.code;
      this.receivedAt = this.clock();
      const me = this.me;
      if (me) this.isHost = me.isHost;
      this.changed();
    });
    bus.on('round:end', (e) => {
      this.roundEnd = e;
      if (!this.roundEnds.some((r) => r.roundId === e.roundId)) this.roundEnds.push(e);
      this.changed();
    });
    bus.on('match:end', (e) => {
      this.matchEnd = e;
      this.changed();
    });
  }

  /** Called after any room change (screens re-render lists here, not per frame). */
  onChange(fn: () => void): void {
    this.listeners.push(fn);
  }

  get inRoom(): boolean {
    return this.code !== '';
  }

  get me(): PlayerDTO | null {
    return this.dto && this.slot >= 0 ? this.dto.players[this.slot] ?? null : null;
  }

  get phase(): RoomPhase | null {
    return this.dto ? this.dto.phase : null;
  }

  /** Room tick now, extrapolated from the last room message. */
  roomTick(): number {
    return this.dto ? this.dto.roomTick + (this.clock() - this.receivedAt) / TICK_MS : 0;
  }

  /** Seconds until a room tick (countdowns), never negative. */
  secondsUntil(tick: number): number {
    return Math.max(0, (tick - this.roomTick()) / TICK_HZ);
  }

  humans(): number {
    return this.dto ? this.dto.players.filter((p) => p !== null && !p.isBot).length : 0;
  }

  reset(): void {
    this.dto = null;
    this.code = '';
    this.slot = -1;
    this.spectator = false;
    this.isHost = false;
    this.roundEnd = null;
    this.roundEnds.length = 0;
    this.matchEnd = null;
    this.myVote = -1;
    this.changed();
  }

  private changed(): void {
    for (const fn of this.listeners) fn();
  }
}
